import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { service } from '@ember/service';
import { buildWaiter } from '@ember/test-waiters';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { format, isValid, parseISO } from 'date-fns';
import { didCancel, dropTask, enqueueTask, restartableTask, task, timeout } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import { runTask } from 'ember-lifeline';
import { modifier } from 'ember-modifier';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { and, eq, not, or } from 'ember-truth-helpers';
import DirectUploader from 'garaje/components/direct-uploader';
import EnvoyOneUpCalendar from 'garaje/components/envoy-one-up-calendar';
import UiCard from 'garaje/components/ui/card';
import DocumentDeleteConfirmationModal from 'garaje/components/visitors/document-delete-confirmation-modal';
import type { DocumentStatus } from 'garaje/components/visitors/document-status-badge';
import DocumentStatusBadge from 'garaje/components/visitors/document-status-badge';
import DocumentPreviewModal from 'garaje/components/visitors/profiles/document-preview-modal';
import type { ProfileDocument } from 'garaje/components/visitors/profiles/document-status';
import type CustomUserDocumentTemplateModel from 'garaje/models/custom-user-document-template';
import type DraftDocumentModel from 'garaje/models/draft-document';
import type { DraftDocumentFieldValues, DraftDocumentStatus } from 'garaje/models/draft-document';
import type UserDocumentTemplateConfigurationModel from 'garaje/models/user-document-template-configuration';
import type VisitorDocumentModel from 'garaje/models/visitor-document';
import { uploadFile } from 'garaje/utils/document-uploads';
import { confirmDraftDocument, pollDraftDocumentWithDeadline, requestUploadLink } from 'garaje/utils/draft-documents';
import {
  confirmVisitorDocument,
  pollVisitorDocumentWithDeadline,
  requestVisitorDocUploadLink,
  type VisitType,
} from 'garaje/utils/visitor-documents';
import type { ValidationResult } from 'utils/direct-uploader';

// Re-exported so the list (and tests) share one canonical shape (A2's model).
export type { DraftDocumentFieldValues, DraftDocumentStatus };

// Client-side dropzone constraints for admin pre-reg draft documents.
// 10 MB client cap (the backend allows 100 MB) — accept PDF / JPG / PNG.
const ACCEPTED_FILE_TYPES = '.pdf,.jpg,.jpeg,.png';
const ACCEPTED_MIME_REGEX = /^(application\/pdf|image\/jpeg|image\/png)$/;
const MAX_FILE_SIZE_BYTES = 10_000_000;

// The upload mixes ED/jQuery calls (tracked by `settled()`) with a raw-XHR S3 PUT and an
// ember-concurrency `timeout()` poll — neither of which `settled()` observes (EC registers no
// test-waiter). Without an explicit waiter, `await triggerEvent('change')`/`settled()` can return
// mid-upload, before `@onDraftChange` reports the draft id to the spine, so the invite save races
// ahead of the collect. This waiter makes `settled()` block until the whole task (incl. the final
// `onDraftChange`) completes. Compiles to a no-op in production.
const uploadWaiter = buildWaiter('document-review-card:uploadDraftTask');

type ExpirationEntry = { name: string; date: Date | null };
/** A row with both a name and a parsed date — the output of `parseEntries`. */
type CompleteExpirationEntry = { name: string; date: Date };

// A row is XOR-incomplete: exactly one of name/date filled. Pure (reads its argument, not
// `this.expirationEntries`) so the write chokepoint can call it without entangling the seed
// modifier — that read would re-seed and wipe edits on every keystroke.
function listHasIncompleteRow(entries: readonly ExpirationEntry[]): boolean {
  return entries.some((e) => Boolean(e.name) !== (e.date !== null));
}

/**
 * Per-card create-mode lifecycle phase. Loading/parsing/error UI derives from THIS
 * (the task lifecycle), not the draft model's raw `status` — a failed or timed-out
 * parse must leave a recoverable state, not a perpetual spinner.
 */
type UploadPhase = 'idle' | 'uploading' | 'parsing' | 'completed' | 'error';

export type DocumentBusyKind = 'upload' | 'field-values-save' | 'incomplete';

/**
 * The card's render mode, shared with DocumentReviewList so the two can't drift.
 * 'review' (default) = approve/deny an uploaded doc. 'create' = admin pre-reg upload + parse.
 * 'manage' = edit an existing visit's docs (upload/delete); review actions still show for a pending doc.
 */
export type DocumentReviewMode = 'review' | 'create' | 'manage';

interface Signature {
  Args: {
    document?: VisitorDocumentModel;
    template?: CustomUserDocumentTemplateModel;
    configuration?: UserDocumentTemplateConfigurationModel;
    isUploaded?: boolean;
    showUndo?: boolean;
    onStatusChange?: () => void;
    mode?: DocumentReviewMode;
    /** Create mode — required for the upload link. */
    locationId?: string | null;
    /** Create mode — the selected flow, sent as a relationship on confirm. */
    flowId?: string | null;
    /**
     * Create mode — reports this card's draft id up to the spine so the invite-create can
     * reference it via the write-only `draft-documents` relationship. Fires as soon as the
     * draft exists (parsing never blocks submit), and with `null` on remove/replace OR when
     * an upload/parse error leaves no usable draft.
     */
    onDraftChange?: (templateId: string, draftId: string | null) => void;
    /**
     * Create mode — reports whether THIS card has an upload in flight: `true` while the file
     * is uploading (the pre-confirm window where no draft id exists yet), `false` once the
     * draft is created (parsing onward), on error, and on remove. The spine aggregates these
     * to disable Submit while any document is still uploading — otherwise the invite could be
     * created before the draft id is collected, silently dropping the document. Parsing does
     * NOT count as in-flight (the id is already reported, and the design allows submit then).
     *
     * `kind` distinguishes the two busy classes so the spine can gate them differently:
     * 'upload' (seconds-long, hard-blocks submit) vs 'field-values-save' (a ~200ms PATCH the
     * spine's saveTask waits out, so the click that committed an edit can also submit).
     */
    onUploadStateChange?: (templateId: string, isUploading: boolean, kind: DocumentBusyKind) => void;
    /**
     * Optional controlled override (used by isolated component tests): when provided these
     * take precedence over the self-driven draft so rows/parsing can be rendered without
     * standing up the transport.
     */
    status?: DraftDocumentStatus;
    fieldValues?: DraftDocumentFieldValues;
    /** Test seam: shrink the parse-poll interval/timeout so loop/timeout paths run fast. */
    pollOptions?: { intervalMs?: number; timeoutMs?: number };
    /** Manage mode — which visit (invite XOR entry) a direct upload attaches to. */
    visitType?: VisitType;
    /** Manage mode — the id of the visit the upload attaches to. */
    visitId?: string;
  };
  Element: HTMLDivElement;
}

export default class DocumentReviewCard extends Component<Signature> {
  @service declare store: StoreService;

  @tracked expirationEntries: ExpirationEntry[] = [];
  /** Tracks which calendar popover is open: a numeric index, or null */
  @tracked openCalendar: number | null = null;
  // True while keyboard focus is inside the expiration rows — a half-filled row is the
  // NORMAL mid-entry state (name committed, date not yet picked), so the incomplete-row
  // error must not interrupt until the user actually leaves the rows.
  @tracked isEditingExpirationRows = false;
  private expirationFieldsElement: HTMLElement | null = null;

  // ----- create-mode state -----
  @tracked draft: DraftDocumentModel | null = null;
  @tracked selectedFile: File | null = null;
  @tracked previewDataUrl: string | null = null;
  // Blob URL for the in-memory PDF preview iframe. Chrome refuses to render a
  // base64 `data:` PDF in an iframe, so a `blob:` object URL is used instead;
  // it must be revoked on reset/teardown to avoid leaking the object.
  @tracked previewObjectUrl: string | null = null;
  @tracked uploadPhase: UploadPhase = 'idle';
  @tracked uploadErrorMessage: string | null = null;
  @tracked saveErrorMessage: string | null = null;
  // Last upload-in-flight state reported to the spine (dedupes change notifications).
  private busyReported: Record<DocumentBusyKind, boolean> = {
    upload: false,
    'field-values-save': false,
    incomplete: false,
  };

  // Exposed for the dropzone template.
  readonly acceptedFileTypes = ACCEPTED_FILE_TYPES;
  readonly acceptedMimeRegex = ACCEPTED_MIME_REGEX;
  readonly maxFileSizeBytes = MAX_FILE_SIZE_BYTES;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    this.initExpirationEntries();
  }

  get isCreateMode(): boolean {
    return this.args.mode === 'create';
  }

  get isManageMode(): boolean {
    return this.args.mode === 'manage';
  }

  // Per-template test hook for the create form (the `[data-test-admin-prereg-document="<id>"]` seam).
  get adminPreregTemplateId(): string | undefined {
    return this.isCreateMode ? this.args.template?.id : undefined;
  }

  // ----- create-mode display state -----
  // Live path is driven by `uploadPhase`; isolated tests drive it via `@status` (no live task).
  get createCompleted(): boolean {
    return this.isCreateMode && (this.args.status === 'completed' || this.uploadPhase === 'completed');
  }

  get createInProgress(): boolean {
    if (!this.isCreateMode) return false;
    return (
      this.args.status === 'processing' ||
      this.args.status === 'pending' ||
      this.uploadPhase === 'uploading' ||
      this.uploadPhase === 'parsing'
    );
  }

  // Recoverable failure (upload error or parse timeout). Controlled tests have no error state.
  get createErrored(): boolean {
    return this.isCreateMode && this.uploadPhase === 'error';
  }

  // Manage mode shows the dropzone only for a still-missing required doc; once a doc exists the
  // card renders the uploaded-doc view (review rows + delete) instead.
  get showManageUpload(): boolean {
    // Hide the dropzone once an upload has completed (until the tab reload lands the new
    // @document); otherwise the brief completed-but-not-yet-reloaded window re-exposes the
    // dropzone and a second drop would create a duplicate auto-approved doc.
    return this.isManageMode && !this.args.document && this.uploadPhase !== 'completed';
  }

  // Manage upload is in flight (uploading the file or polling the confirmed doc); drives the
  // parsing indicator in place of the dropzone.
  get manageUploadInProgress(): boolean {
    return this.isManageMode && (this.uploadPhase === 'uploading' || this.uploadPhase === 'parsing');
  }

  // The manage upload finished (the doc is settled in the store) but the parent tab reload that
  // threads @document hasn't landed — it failed or is still settling. Surfaces a manual retry
  // instead of the misleading "Document not yet uploaded" placeholder; the dropzone stays hidden so
  // a re-drop can't create a duplicate auto-approved doc.
  get manageUploadAwaitingReload(): boolean {
    return this.isManageMode && !this.args.document && this.uploadPhase === 'completed';
  }

  // Re-run the parent tab reload so the card re-renders with @document. Safe to repeat: it reloads,
  // it does not re-upload.
  retryManageReload = (): void => {
    this.args.onStatusChange?.();
  };

  // Unique input id per required-document card so multiple dropzones don't collide on
  // DirectUploader's default id="file-upload".
  get uploadInputId(): string {
    return `draft-upload-${this.args.template?.id ?? 'new'}`;
  }

  // Accessible name for the (visually-hidden) file input, keyed off the document title so
  // each required-document dropzone is distinctly named for screen-reader / keyboard users.
  get uploadInputAriaLabel(): string {
    const name = this.args.template?.title ?? 'document';
    return `Upload ${name} (PDF, JPG, or PNG, 10 MB max)`;
  }

  // A completed parse that surfaced no dates (vs. dates the admin simply hasn't entered yet),
  // so the lone blank row isn't mistaken for a bug. Keyed off the seeded rows — the exact thing
  // the hint explains — so it matches whatever `parseEntries` produced (an empty map AND a map of
  // unparseable values both collapse to the single auto-inserted blank row).
  get parseReturnedNoDates(): boolean {
    if (!this.createCompleted) return false;
    const [only] = this.expirationEntries;
    return this.expirationEntries.length === 1 && !only?.name && !only?.date;
  }

  private parseDate(dateStr: string): Date | null {
    // Extract date portion to avoid UTC off-by-one for ISO timestamps
    // e.g. "2026-10-15T00:00:00.000Z" should parse as Oct 15, not Oct 14 in PST
    const dateMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
    if (dateMatch) {
      const d = new Date(Number(dateMatch[1]), Number(dateMatch[2]) - 1, Number(dateMatch[3]));
      return isNaN(d.getTime()) ? null : d;
    }
    const parsed = parseISO(dateStr);
    return isValid(parsed) ? parsed : null;
  }

  // Parse a free-form `{ label -> date-string }` map into complete (name + date) rows.
  private parseEntries(data: Record<string, unknown>): CompleteExpirationEntry[] {
    return Object.entries(data)
      .filter(([, v]) => typeof v === 'string')
      .map(([name, dateStr]) => ({ name, date: this.parseDate(String(dateStr)) }))
      .filter((e): e is CompleteExpirationEntry => e.date !== null);
  }

  // One-shot guard for `seedFromDocumentData`: once the review/manage rows have been seeded from the
  // document's parsed field-values, a later auto-save PATCH (which reassigns inputFieldsData) must not
  // re-seed and clobber edits.
  private seededFromDocument = false;

  private initExpirationEntries(): void {
    const parsed = this.parseEntries(this.args.document?.inputFieldsData ?? {});
    // Mounted with an already-parsed doc (e.g. reopening an invite whose doc parsed earlier) → the
    // constructor seeded the real rows, so the reactive re-seed below must stay a no-op.
    if (parsed.length > 0) this.seededFromDocument = true;
    this.#setExpirationEntries(
      parsed.length === 0 && this.requiresExpirationDate ? [{ name: '', date: null }] : parsed,
    );
  }

  // Seed editable rows from a completed parse. On create we always surface at least one row
  // so the admin can hand-enter dates when the parse found nothing (there is no `failed`
  // state — empty is a valid outcome). `fieldValues` is frozen on the model; we only read it.
  private seedExpirationEntriesFromMap(data: Record<string, unknown>): void {
    const parsed = this.parseEntries(data);
    this.#setExpirationEntries(parsed.length > 0 ? parsed : [{ name: '', date: null }]);
  }

  // Controlled-override seeding ONLY: isolated tests pass @status/@fieldValues with no draft.
  // The live path seeds in `uploadDraftTask` instead — our own `updateFieldValues` PATCH
  // reassigns `draft.fieldValues`, so a modifier keyed on it would re-seed and clobber an
  // in-progress (added/edited) row on every auto-save. Mode-gated so a stray @status in
  // review mode can't overwrite rows seeded from the uploaded doc's `inputFieldsData`.
  seedFromControlledArgs = modifier(
    (
      _element: Element,
      [status, fieldValues]: [DraftDocumentStatus | undefined, DraftDocumentFieldValues | undefined],
    ) => {
      if (!this.isCreateMode || status !== 'completed') return;
      this.seedExpirationEntriesFromMap(fieldValues ?? {});
    },
  );

  // Review/manage mode seeds expiration rows from `@document.inputFieldsData`. The constructor seeds
  // once at mount; but when an admin uploads in-session the parsed doc lands via a PARENT reload that
  // REUSES this card instance (the manage list keys by template id), so the constructor never re-runs
  // and the dates wouldn't render until a tab switch remounted the card. This modifier seeds the first
  // time the rows render with the reloaded doc. One-shot (`seededFromDocument`) so the later auto-save
  // PATCH — which reassigns `inputFieldsData` — can never re-seed and clobber edits. Manage rows only
  // render after the post-parse reload (`showExpirationRows` ⇒ `@isUploaded`), so this first run always
  // carries the final parsed data.
  seedFromDocumentData = modifier((_element: Element, [data]: [Record<string, unknown> | undefined]) => {
    if (this.isCreateMode || this.seededFromDocument) return;
    this.seededFromDocument = true;
    const parsed = this.parseEntries(data ?? {});
    if (parsed.length > 0) this.#setExpirationEntries(parsed);
  });

  get title(): string | undefined {
    return this.args.isUploaded ? this.args.document?.title : this.args.template?.title;
  }

  get status(): DocumentStatus {
    if (!this.args.isUploaded) return 'not-uploaded';
    const doc = this.args.document;
    if (doc?.isApproved) return 'approved';
    if (doc?.isDenied) return 'denied';
    // Manage mode is a superset of review: a still-pending doc (e.g. a visitor pre-reg upload on a
    // required template) keeps its true 'review' status so the reviewer can still act on it — manage
    // adds upload/delete, it does not mask approval state. An admin-uploaded doc is already isApproved
    // above.
    return 'review';
  }

  // Review mode always shows the approval badge. Create mode shows "NOT UPLOADED" only at
  // rest (nothing selected/uploading/done) — the post-convert APPROVED badge belongs to the
  // review/detail path, not the create form.
  get showStatusBadge(): boolean {
    // The create form never shows a status badge: the empty state is just the
    // dropzone, and the post-convert APPROVED badge belongs to the review/detail path.
    if (this.isCreateMode) return false;
    return true;
  }

  get uploadDate(): string | null {
    const attachment = this.args.document?.mostRecentAttachment;
    const date = attachment?.updatedAt;
    if (!date) {
      const docDate = this.args.document?.updatedAt;
      return docDate ? format(docDate, 'MMM d, yyyy, h:mm a') : null;
    }
    return format(date, 'MMM d, yyyy, h:mm a');
  }

  get externalAttachmentUrl(): string | null {
    return this.args.document?.externalAttachments?.[0]?.url ?? null;
  }

  @tracked imageFailedToLoad = false;

  get isPdf(): boolean {
    return this.imageFailedToLoad;
  }

  // True only when we have a renderable in-memory image preview. Falls back to the filename
  // tile for PDFs AND for images whose FileReader preview never produced a data URL.
  get hasImagePreview(): boolean {
    return Boolean(this.selectedFile?.type.startsWith('image/')) && this.previewDataUrl !== null;
  }

  // In-memory PDF preview source: blob URL + the same viewer-chrome-hiding hash
  // the review-mode iframe uses, so the dropped file renders as a clipped first
  // page rather than a generic file tile.
  get createPreviewPdfUrl(): string | null {
    return this.previewObjectUrl ? `${this.previewObjectUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH` : null;
  }

  // The document is "discarded" once the backend has anonymized the
  // attachment, signalled by an `anonymized_at` timestamp on the first
  // external attachment. When set, the preview area renders the "Document
  // discarded" placeholder instead of the regular preview/PDF.
  get isDiscarded(): boolean {
    // Top-level anonymizedAt is set for both Veriff and profile docs. Fall back to
    // the per-attachment flag; redacted Veriff docs have an empty externalAttachments
    // array, so the per-attachment check alone would miss them.
    return !!(this.args.document?.anonymizedAt ?? this.args.document?.externalAttachments?.[0]?.anonymized_at);
  }

  get mediaOverlayClass(): string {
    return 'absolute inset-0 flex items-center justify-center gap-2 bg-black/0 opacity-0 group-hover:bg-black/40 group-hover:opacity-100 transition-all duration-200 pointer-events-none';
  }

  @action handleImageError(): void {
    this.imageFailedToLoad = true;
  }

  get pdfViewerUrl(): string {
    // view=FitH (same as the create preview) so the page fills the frame width and the bottom
    // gray band lifts up over the document — without it the page letterboxes and the band sits below.
    return `${this.externalAttachmentUrl}#navpanes=0&toolbar=0&scrollbar=0&view=FitH`;
  }

  get documentFileName(): string {
    return this.title ?? 'document.pdf';
  }

  @tracked showPreviewModal = false;

  get previewDocuments(): ProfileDocument[] {
    return [this.previewDocument];
  }

  get previewDocument(): ProfileDocument {
    return {
      id: this.args.document?.id ?? '',
      title: this.title ?? '',
      downloadUrl: this.externalAttachmentUrl ?? undefined,
      approvalStatus: this.args.document?.isApproved ? 'approved' : this.args.document?.isDenied ? 'denied' : 'review',
      createdAt: this.args.document?.createdAt?.toISOString(),
      fieldValues: this.args.document?.inputFieldsData,
    };
  }

  @action openDocument(): void {
    this.showPreviewModal = true;
  }

  @action closePreviewModal(): void {
    this.showPreviewModal = false;
  }

  get documentLink(): VisitorDocumentModel['userDocumentLink'] {
    return this.args.document?.userDocumentLink;
  }

  get isPending(): boolean {
    return this.status === 'review';
  }

  get requiresExpirationDate(): boolean {
    return this.args.configuration?.reUploadConfig?.policy === 'on_expiration';
  }

  // Whether LLM extraction of expiration dates is enabled for this document's template. The per-flow
  // configuration overrides the template default (mirrors the backend's flow.llm_parsing_enabled_for).
  // When disabled the backend never parses — the draft stays 'pending' and the visitor-doc reports
  // 'processing' until a timeout — so the FE must skip the parse poll and the "Parsing…" indicator
  // (otherwise the spinner hangs for the full timeout, and create mode would even time out into error).
  get llmParsingEnabled(): boolean {
    return this.args.configuration?.allowLlmParsing ?? this.args.template?.allowLlmParsing ?? false;
  }

  // Editable rows render for an uploaded doc (review) or a completed draft (create).
  get showExpirationRows(): boolean {
    if (this.isCreateMode) return this.createCompleted;
    return Boolean(this.args.isUploaded);
  }

  get parsingLabel(): string {
    return this.uploadPhase === 'uploading' ? 'Uploading…' : 'Parsing document…';
  }

  get hasAnyCompleteDate(): boolean {
    return this.expirationEntries.some((e) => e.name && e.date);
  }

  get approveDisabled(): boolean {
    if (this.approveTask.isRunning) return true;
    if (this.requiresExpirationDate && !this.hasAnyCompleteDate) return true;
    if (this.hasIncompleteExpirationEntries) return true;
    return false;
  }

  formatDate = (date: Date | null): string => (date ? format(date, 'MMM d, yyyy') : 'Select date');

  @action toggleCalendar(id: number): void {
    if (this.openCalendar === id) {
      this.closeCalendar();
    } else {
      this.openCalendar = id;
      // Escape binds synchronously — the opening interaction is a click/Enter, never Escape, so
      // there's no self-dismiss risk. The outside-click listener stays in rAF so the opening
      // click itself doesn't bubble to it and immediately close the popover.
      document.addEventListener('keydown', this.handleEscapeKey, true);
      requestAnimationFrame(() => {
        if (this.isDestroying || this.isDestroyed) return;
        document.addEventListener('click', this.handleOutsideClick, true);
      });
    }
  }

  captureExpirationFields = modifier((element: Element) => {
    this.expirationFieldsElement = element as HTMLElement;
    const onFocusIn = (): void => {
      this.isEditingExpirationRows = true;
    };
    const onFocusOut = (event: Event): void => {
      if (element.contains((event as FocusEvent).relatedTarget as Node)) return;
      this.isEditingExpirationRows = false;
    };
    element.addEventListener('focusin', onFocusIn);
    element.addEventListener('focusout', onFocusOut);
    return () => {
      element.removeEventListener('focusin', onFocusIn);
      element.removeEventListener('focusout', onFocusOut);
    };
  });

  // Defer the incomplete-row error until focus leaves the rows: announcing "both name and
  // date are required" while the admin is between filling the name and picking the date
  // reads as a premature failure (reported by QA as "error pops up early and blocks you").
  // `approveDisabled` / the save-skip still gate on the raw incompleteness immediately.
  get showIncompleteRowError(): boolean {
    return this.hasIncompleteExpirationEntries && !this.isEditingExpirationRows;
  }

  handleOutsideClick = (event: MouseEvent): void => {
    if (this.expirationFieldsElement?.contains(event.target as Node)) return;
    this.closeCalendar();
  };

  // Escape closes the open calendar popover and returns focus to the date trigger that opened it
  // (WAI-ARIA dialog pattern), so keyboard users can dismiss it without reaching for the mouse.
  handleEscapeKey = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') return;
    const triggerIndex = this.openCalendar;
    event.stopPropagation();
    this.closeCalendar();
    if (triggerIndex === null) return;
    runTask(this, () => {
      const triggers = this.expirationFieldsElement?.querySelectorAll<HTMLElement>('[data-test-expiration-date]');
      triggers?.[triggerIndex]?.focus();
    });
  };

  closeCalendar = (): void => {
    this.openCalendar = null;
    document.removeEventListener('click', this.handleOutsideClick, true);
    document.removeEventListener('keydown', this.handleEscapeKey, true);
  };

  willDestroy(): void {
    super.willDestroy();
    document.removeEventListener('click', this.handleOutsideClick, true);
    document.removeEventListener('keydown', this.handleEscapeKey, true);
    // An upload can be in flight while the card is destroyed (e.g. started during the
    // flow-switch async window, before the new flow's templates land). Retract the
    // in-flight report so the spine's Submit gate isn't left disabled forever.
    this.reportUploadInFlight(false);
    this.reportUploadInFlight(false, 'field-values-save');
    this.reportUploadInFlight(false, 'incomplete');
    // Cancel an in-flight delete so its post-await tracked writes can't fire on a torn-down card
    // (the task's own isDestroying guard is the primary protection; this stops the round-trip too).
    void this.deleteDocumentTask.cancelAll();
    this.#setPreviewObjectUrl(null);
  }

  // Revoke the prior blob URL before replacing it (and on teardown) so the
  // in-memory PDF object isn't leaked across re-selections.
  #setPreviewObjectUrl(url: string | null): void {
    if (this.previewObjectUrl) URL.revokeObjectURL(this.previewObjectUrl);
    this.previewObjectUrl = url;
  }

  get isDecided(): boolean {
    return this.status === 'approved' || this.status === 'denied';
  }

  @action addExpirationRow(): void {
    this.#setExpirationEntries([...this.expirationEntries, { name: '', date: null }]);
  }

  get canRemoveRow(): boolean {
    return !this.requiresExpirationDate || this.expirationEntries.length > 1;
  }

  @action removeExpirationRow(index: number): void {
    if (!this.canRemoveRow) return;
    this.#setExpirationEntries(this.expirationEntries.filter((_, i) => i !== index));
    void this.saveExpirationDatesTask.perform();
    // Removing the row destroys the focused remove button; move focus to the successor row's
    // remove button (or the Add button when no removable rows remain) once the rows re-render,
    // so keyboard/SR users aren't dropped on <body>. Mirrors resetFile's focus restoration.
    runTask(this, () => {
      const container = this.expirationFieldsElement;
      if (!container) return;
      const removeButtons = container.querySelectorAll<HTMLElement>('[data-test-remove-expiration]');
      const successor = removeButtons[Math.min(index, removeButtons.length - 1)];
      (successor ?? container.querySelector<HTMLElement>('[data-test-add-expiration]'))?.focus();
    });
  }

  @action updateExpirationName(index: number, event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.#setExpirationEntries(this.expirationEntries.map((e, i) => (i === index ? { ...e, name: value } : e)));
    void this.saveExpirationDatesTask.perform();
  }

  @action selectExpirationDate(index: number, date: Date): void {
    this.#setExpirationEntries(this.expirationEntries.map((e, i) => (i === index ? { ...e, date } : e)));
    this.closeCalendar();
    void this.saveExpirationDatesTask.perform();
  }

  // Build a fresh `{ label -> yyyy-MM-dd }` map from the current rows. Always a new
  // object — never mutates the (frozen) draft `fieldValues`.
  private buildInputFieldsData(): Record<string, string> {
    const data: Record<string, string> = {};
    for (const entry of this.expirationEntries) {
      if (entry.name && entry.date) {
        data[entry.name] = format(entry.date, 'yyyy-MM-dd');
      }
    }
    return data;
  }

  get hasIncompleteExpirationEntries(): boolean {
    return listHasIncompleteRow(this.expirationEntries);
  }

  // Single chokepoint for every expirationEntries write, so the create-mode incomplete-row signal
  // to the spine can't be bypassed by any current or future mutation site.
  #setExpirationEntries(next: ExpirationEntry[]): void {
    this.expirationEntries = next;
    this.reportUploadInFlight(listHasIncompleteRow(next), 'incomplete');
  }

  // Persist edited expiration dates. Create mode PATCHes the draft via its model
  // apiAction; review mode PATCHes the visitor-document.
  private async persistFieldValues(): Promise<void> {
    if (this.isCreateMode) {
      if (!this.draft) return;
      await this.draft.updateFieldValues(this.buildInputFieldsData());
      return;
    }
    await this.patchInputFieldsData();
  }

  // Send a targeted PATCH with input_fields_data. Include `active` to
  // preserve its current value — the response pushes all attributes
  // back into the store.
  private async patchInputFieldsData(): Promise<void> {
    const doc = this.args.document;
    if (!doc) return;
    const data = this.buildInputFieldsData();
    const adapter = this.store.adapterFor('visitor-document');
    const url = adapter.buildURL('visitor-document', doc.id, null, 'v3');
    await adapter.ajax(url, 'PATCH', {
      data: {
        data: {
          type: 'visitor-documents',
          id: doc.id,
          attributes: {
            'input-fields-data': data,
            active: doc.active,
          },
        },
      },
    });
    doc.inputFieldsData = Object.freeze({ ...data });
  }

  // Eager auto-save: persists on every committed edit (no debounce) so edits land before a
  // human-paced invite submit — there is nothing deferred to "flush" at submit time, and the
  // async draft->document convert reads the freshly-PATCHed field_values. enqueueTask (NOT
  // restartable): EC cancellation can't abort the in-flight adapter.ajax PATCH, so a restart
  // would leave two full-map PATCHes racing and an older one could land last. enqueue runs saves
  // strictly FIFO — the next PATCH dispatches only after the prior resolves — so the last write to
  // the server is genuinely the newest, and `approveTask` routes its persist through this same
  // queue so an edit-then-approve can't overlap either. Skips incomplete rows. On a real failure,
  // surface it to the admin (auto-save is silent — they'd otherwise believe a compliance date
  // saved when it didn't) AND rethrow for telemetry.
  saveExpirationDatesTask = enqueueTask(async () => {
    // The invite convert reads field-values from the draft SERVER-side, so a submit must
    // not race an in-flight PATCH (an auto-approved compliance doc would carry stale dates).
    // Report this card busy through the same channel as uploads; clear once the FIFO drains.
    this.reportUploadInFlight(true, 'field-values-save');
    try {
      if (this.hasIncompleteExpirationEntries) return;
      this.saveErrorMessage = null;
      await this.persistFieldValues();
    } catch (e) {
      if (didCancel(e)) return;
      this.saveErrorMessage = 'Could not save expiration dates. Please try again.';
      throw e;
    } finally {
      if (!this.saveExpirationDatesTask.isQueued) this.reportUploadInFlight(false, 'field-values-save');
    }
  });

  // ----- create-mode upload/parse lifecycle (per-card) -----

  // Notify the spine when this card's in-flight state changes (only on change), so it can
  // disable Submit while any document is still uploading (the pre-confirm window) OR a
  // field-values PATCH is pending (the convert reads field-values server-side at save time).
  private reportUploadInFlight(isUploading: boolean, kind: DocumentBusyKind = 'upload'): void {
    const templateId = this.args.template?.id;
    if (!this.isCreateMode || !templateId) return;
    if (this.busyReported[kind] === isUploading) return;
    this.busyReported[kind] = isUploading;
    this.args.onUploadStateChange?.(templateId, isUploading, kind);
  }

  handleValidate = (result: ValidationResult): void => {
    if (!result.sizeValid) this.uploadErrorMessage = 'The file must be 10 MB or less.';
    else if (!result.typeValid) this.uploadErrorMessage = 'The file must be a PDF, JPG, or PNG.';
    else this.uploadErrorMessage = null;
  };

  handleFilePreview = (result: FileReader['result']): void => {
    this.previewDataUrl = typeof result === 'string' ? result : null;
  };

  // DirectUploader only invokes this for files that passed validation.
  handleFileSelected = (file: File | null): void => {
    if (!file) return;
    // Manage mode uploads the file straight to the existing visit — no draft, no in-memory
    // preview/seeding: onStatusChange reloads the tab and the card re-renders with @document.
    if (this.isManageMode) {
      this.uploadPhase = 'uploading';
      void this.uploadVisitorDocTask.perform(file);
      return;
    }
    this.selectedFile = file;
    // PDFs render via a blob-URL iframe (Chrome blocks data: PDFs in frames);
    // images use the data-URL preview, so only mint an object URL for PDFs.
    this.#setPreviewObjectUrl(file.type === 'application/pdf' ? URL.createObjectURL(file) : null);
    // Set the phase synchronously so the dropzone doesn't flicker back before the task starts.
    this.uploadPhase = 'uploading';
    this.reportUploadInFlight(true);
    void this.uploadDraftTask.perform(file);
  };

  resetFile = (): void => {
    void this.uploadDraftTask.cancelAll();
    // Also drop any queued/in-flight expiration saves: a save that fails AFTER the
    // reset would otherwise set saveErrorMessage on the now-empty card.
    void this.saveExpirationDatesTask.cancelAll();
    this.selectedFile = null;
    this.previewDataUrl = null;
    this.#setPreviewObjectUrl(null);
    this.draft = null;
    this.#setExpirationEntries([]);
    this.uploadErrorMessage = null;
    this.saveErrorMessage = null;
    this.uploadPhase = 'idle';
    this.reportUploadInFlight(false);
    this.reportUploadInFlight(false, 'field-values-save');
    if (this.args.template) this.args.onDraftChange?.(this.args.template.id, null);
    // Removing the file destroys the focused Remove button; return focus to the dropzone
    // input once it re-renders so keyboard users aren't dropped on <body>.
    runTask(this, () => {
      document.getElementById(this.uploadInputId)?.focus();
    });
  };

  // Per-row accessible names: with multiple coverages every row otherwise carries the
  // same label, so a screen-reader user can't tell which row they're on.
  expirationNameLabel = (index: number): string => `Expiration date name, row ${index + 1}`;

  // Index-only — never interpolate the user/LLM-supplied field name: data-private does not
  // mask aria-label, so a name here would leak to session replay. The name stays only in the
  // data-private visible input.
  expirationDateLabel = (index: number): string => `Expiration date, row ${index + 1}`;

  // Per-row, card-unique id so a name-blank/date-set input's aria-describedby can point at its
  // reason (guidFor keeps it unique across multiple cards on the page).
  expirationNameErrorId = (index: number): string => `${guidFor(this)}-exp-name-error-${index}`;

  // Poll the draft until the terminal `completed` status. Hosted on the card (not a
  // util) so cancellation stays task-linked: `uploadDraftTask` performs this as a
  // child, and a remove/replace or teardown cancels the loop at its next await. A
  // parse failure resolves as `completed` with an empty field-values map (callers
  // use `hasFieldValues`); a parse that exceeds `timeoutMs` REJECTS so a stuck parse
  // surfaces through `.last.error` instead of a silently non-terminal draft.
  pollUntilTerminalTask = task(async (draftDocument: DraftDocumentModel) => {
    const { intervalMs = 1000, timeoutMs = 30000 } = this.args.pollOptions ?? {};
    const startedAt = Date.now();
    let current = draftDocument;

    while (!current.isCompleted) {
      const remaining = timeoutMs - (Date.now() - startedAt);
      if (remaining <= 0) {
        throw new Error(`Draft document ${current.id} parsing timed out after ${timeoutMs}ms`);
      }
      await timeout(Math.min(intervalMs, remaining));
      // Bound the GET by the remaining budget so a hung poll can't skip the
      // next top-of-loop deadline check and hang the task forever.
      current = await pollDraftDocumentWithDeadline(
        this.store,
        current.id,
        Math.max(timeoutMs - (Date.now() - startedAt), 0),
      );
    }

    return current;
  });

  // Per-card upload/parse orchestration — restartable so re-selecting a file replaces
  // the run. Reports the draft id up immediately (the invite-create references it even
  // before parsing finishes), then polls to completion. A failed upload or a parse
  // timeout lands in `error` (recoverable via Remove) — never a perpetual spinner —
  // and retracts the reported id so a doc we couldn't confirm isn't submitted as if ready.
  uploadDraftTask = restartableTask(async (file: File) => {
    // `settled()` is blind to the raw-XHR S3 PUT and the EC `timeout()` poll inside this task;
    // hold a test waiter so submit/save can't race ahead of the final `onDraftChange` collect.
    const waiterToken = uploadWaiter.beginAsync();
    // EC cancellation interrupts THIS task at its awaits but can't reach into the raw-XHR
    // S3 PUT — abort it via the controller in `finally` (which runs on cancel too), so a
    // remove/replace/teardown doesn't leave the presigned upload streaming in the background.
    // Aborting after the PUT settled is a no-op (uploadFile guards re-entry).
    const uploadAbort = new AbortController();
    try {
      this.uploadErrorMessage = null;
      const template = this.args.template;
      const flowId = this.args.flowId;
      if (!template || !flowId) {
        this.uploadPhase = 'error';
        // Two distinct causes, one recoverable UI. A missing `template` is a programming
        // error (the card always renders a loaded template in create mode) — surface it
        // plainly. A missing `flowId` is a not-ready state (the flow hasn't resolved yet)
        // — ask the admin to pick a visitor type. Either way the card lands in the
        // recoverable error state (Remove + retry), never a perpetual spinner.
        this.uploadErrorMessage = !flowId
          ? 'Select a visitor type before uploading a document.'
          : 'Unable to upload right now. Please try again.';
        // The change handler already reported the upload in-flight (disabling Submit);
        // retract it here so this guarded early-return doesn't leave Submit stuck disabled
        // — every other exit path (success at line ~708, catch at ~728) does the same.
        this.reportUploadInFlight(false);
        return;
      }
      // Run-local so the catch below branches on THIS run's progress — `this.draft` could
      // hold a stale draft from a previous upload when a replacement file's upload fails.
      let created: DraftDocumentModel | null = null;
      try {
        this.uploadPhase = 'uploading';
        // request-link → upload → confirm are sequenced here (not in a util compose)
        // so each await is a cancellation point of THIS task: a remove/replace or
        // teardown mid-upload stops before the next step fires.
        const { draftDocumentId, uploadLink } = await requestUploadLink(this.store, {
          templateId: template.id,
          locationId: this.args.locationId,
          contentType: file.type,
          byteSize: file.size,
        });
        await uploadFile(uploadLink, file, { signal: uploadAbort.signal });
        created = await confirmDraftDocument(this.store, draftDocumentId, { templateId: template.id, flowId });
        this.draft = created;
        // Draft now exists — leave the pre-confirm "uploading" window so Submit re-enables
        // (parsing does not block submit; the id is reported below for invite-create).
        this.reportUploadInFlight(false);
        this.args.onDraftChange?.(template.id, created.id);

        let finalDraft = created;
        if (this.llmParsingEnabled) {
          this.uploadPhase = 'parsing';
          // Resolves only when the parse reaches `completed`; pollUntilTerminalTask REJECTS on
          // timeout (handled in catch). A parse that found nothing still resolves as completed
          // with an empty fieldValues map -> one blank row for manual entry (no `failed` status,
          // and none is needed here — empty and not-yet-entered look the same to the admin).
          finalDraft = await this.pollUntilTerminalTask.perform(created);
          this.seedExpirationEntriesFromMap(finalDraft.fieldValues ?? {});
        } else {
          // Extraction disabled for this template: the backend never parses, so the draft stays
          // 'pending' forever — polling would hang then time out into the error state. Skip straight
          // to completed and seed one blank row for manual entry.
          this.seedExpirationEntriesFromMap({});
        }
        this.draft = finalDraft;
        this.uploadPhase = 'completed';
        this.args.onDraftChange?.(template.id, finalDraft.id);
      } catch (e) {
        if (didCancel(e)) return;
        // Recoverable failure. If the draft was created, the parse/poll failed (e.g. timeout);
        // otherwise the upload itself failed. Retract the id either way — the admin Removes + retries.
        this.uploadPhase = 'error';
        this.uploadErrorMessage = created
          ? 'Parsing took too long. Remove the file and try again.'
          : 'Upload failed. Please try again.';
        this.reportUploadInFlight(false);
        this.args.onDraftChange?.(template.id, null);
      }
    } finally {
      uploadAbort.abort();
      uploadWaiter.endAsync(waiterToken);
    }
  });

  // Poll the just-confirmed visitor-document until its async expiration-date parse lands
  // (processing-status === 'completed'), so the tab reload that follows renders a doc whose
  // input-fields-data is populated. Unlike the draft poll this RETURNS (not throws) on timeout:
  // the doc already exists + is approved, so a slow parse reconciles (reload now; dates fill on a
  // later view) rather than surfacing an error. Hosted on the card so EC cancels it automatically
  // when the component is torn down — the loop stops at the next await, it does not keep polling a
  // destroyed card. dropTask (like the sibling action tasks) so a re-trigger can't overlap polls.
  pollVisitorDocUntilParsedTask = dropTask(async (visitorDocument: VisitorDocumentModel) => {
    const { intervalMs = 1000, timeoutMs = 30000 } = this.args.pollOptions ?? {};
    const startedAt = Date.now();
    let current = visitorDocument;
    while (!current.isParseComplete) {
      const remaining = timeoutMs - (Date.now() - startedAt);
      if (remaining <= 0) return current;
      await timeout(Math.min(intervalMs, remaining));
      current = await pollVisitorDocumentWithDeadline(
        this.store,
        current.id,
        Math.max(timeoutMs - (Date.now() - startedAt), 0),
      );
    }
    return current;
  });

  // Manage-mode direct upload: attach the file to the existing visit, then reload the tab.
  // Simpler than the create flow — no draft reservation, no spine reporting, no inline seeding.
  // restartableTask is correct here (unlike the FIFO write-path rule): re-selecting a file cancels
  // the prior run, and the only long-lived step EC cancellation can't reach — the raw-XHR S3 PUT —
  // is aborted explicitly via the AbortController below. The link-request and confirm are short
  // store calls EC cancellation stops; a superseded confirm that still lands server-side is the
  // duplicate the manage list now renders + makes deletable.
  uploadVisitorDocTask = restartableTask(async (file: File) => {
    // EC cancellation can't reach the raw-XHR S3 PUT — abort it via the controller on
    // cancel/teardown so a replace/teardown doesn't leave the presigned upload streaming.
    const uploadAbort = new AbortController();
    // Once confirm resolves, the visitor-document exists AND is auto-approved server-side, so a
    // later (poll) failure must NOT present "upload failed" or re-expose the dropzone — either
    // would prompt the admin to upload a second, duplicate auto-approved doc. Past this point we
    // reconcile via a tab reload instead of surfacing an error.
    let confirmed = false;
    // True once the S3 PUT succeeds. Past this point the confirm may have created the auto-approved
    // doc server-side even if its await later rejects (request deadline, dropped response,
    // normalization throw) — so a failure must reconcile, not invite a duplicate.
    let uploadedToS3 = false;
    try {
      this.uploadErrorMessage = null;
      const template = this.args.template;
      const visitType = this.args.visitType;
      const visitId = this.args.visitId;
      if (!template || !visitType || !visitId) {
        this.uploadPhase = 'error';
        this.uploadErrorMessage = 'Unable to upload right now. Please try again.';
        return;
      }
      try {
        this.uploadPhase = 'uploading';
        const { documentId, uploadLink } = await requestVisitorDocUploadLink(this.store, {
          visitType,
          visitId,
          templateId: template.id,
          contentType: file.type,
          byteSize: file.size,
        });
        await uploadFile(uploadLink, file, { signal: uploadAbort.signal });
        uploadedToS3 = true;
        const doc = await confirmVisitorDocument(this.store, documentId, {
          visitType,
          visitId,
          templateId: template.id,
        });
        confirmed = true;
        if (this.llmParsingEnabled) {
          this.uploadPhase = 'parsing';
          // Wait for the async expiration-date parse to land (processing-status === 'completed')
          // BEFORE the tab reload, so the reloaded doc carries its extracted input-fields-data
          // (the parse runs after confirm; reloading immediately would render empty rows).
          await this.pollVisitorDocUntilParsedTask.perform(doc);
        }
        // Extraction off → no parse runs (the visitor-doc reports 'processing' until a 30s timeout),
        // so skip the poll and reload immediately rather than spinning the "Parsing…" indicator.
        this.uploadPhase = 'completed';
        this.args.onStatusChange?.();
      } catch (e) {
        if (didCancel(e)) return;
        if (confirmed) {
          // The document was created + approved; only the post-confirm reload poll failed
          // (transient). Reconcile by reloading the tab — which renders the new doc — instead of
          // showing an error + dropzone that would invite a duplicate auto-approved upload.
          this.uploadPhase = 'completed';
          this.args.onStatusChange?.();
          return;
        }
        if (uploadedToS3 && (await this.confirmCommittedDoc(visitType, visitId, template.id))) {
          // The confirm rejected client-side but committed the doc server-side — reconcile via the
          // tab reload (which renders it) rather than an error + dropzone that would invite a
          // duplicate auto-approved doc.
          this.uploadPhase = 'completed';
          this.args.onStatusChange?.();
          return;
        }
        this.uploadPhase = 'error';
        this.uploadErrorMessage = 'Upload failed. Please try again.';
      }
    } finally {
      uploadAbort.abort();
    }
  });

  // After the S3 PUT the confirm may have created the auto-approved visitor-document server-side
  // yet rejected client-side; re-query the visit's documents to detect that committed doc so the
  // catch reconciles (reload) instead of prompting a duplicate upload.
  private async confirmCommittedDoc(visitType: VisitType, visitId: string, templateId: string): Promise<boolean> {
    try {
      const docs = await this.store.query('visitor-document', { filter: { [`${visitType}-id`]: visitId } });
      // eslint-disable-next-line ember/use-ember-get-and-set
      return docs.slice().some((d) => d.belongsTo('userDocumentTemplate').id() === templateId);
    } catch (e) {
      // Let EC cancellation propagate so a superseded run (file re-selected mid-re-query) stops
      // cleanly instead of falling through to uploadPhase='error' and flashing a stale error.
      if (didCancel(e)) throw e;
      return false;
    }
  }

  approveTask = dropTask(async () => {
    try {
      // Route the final persist through the FIFO save queue (not a separate patch) so it can't
      // overlap an in-flight edit save and land out of order; await it before approving.
      await this.saveExpirationDatesTask.perform();
      await this.documentLink?.approve();
      this.args.onStatusChange?.();
    } catch (e) {
      if (!didCancel(e)) throw e;
    }
  });

  denyTask = dropTask(async () => {
    try {
      void this.saveExpirationDatesTask.cancelAll();
      await this.documentLink?.deny();
      this.args.onStatusChange?.();
    } catch (e) {
      if (!didCancel(e)) throw e;
    }
  });

  resetTask = dropTask(async () => {
    try {
      await this.documentLink?.reset();
      this.args.onStatusChange?.();
    } catch (e) {
      if (!didCancel(e)) throw e;
    }
  });

  @tracked showDeleteConfirm = false;
  @tracked deleteErrorMessage: string | null = null;

  // The delete affordance shows only for an existing doc in manage mode; reaching manage mode is
  // permission-gated upstream (the documents tab renders manage mode only for reviewers + flag).
  get showDeleteAffordance(): boolean {
    return this.isManageMode && Boolean(this.args.document);
  }

  openDeleteConfirm = (): void => {
    this.deleteErrorMessage = null;
    this.showDeleteConfirm = true;
  };

  closeDeleteConfirm = (): void => {
    // Cancel / Close / Esc / click-outside all route here. Ignore dismissal while a delete is
    // in flight so a subsequent failure's error still renders in the open modal instead of being
    // lost to a prematurely-closed modal.
    if (this.deleteDocumentTask.isRunning) return;
    this.showDeleteConfirm = false;
  };

  deleteDocumentTask = dropTask(async () => {
    const doc = this.args.document;
    if (!doc) return;
    // Cancel (fire-and-forget) any in-flight expiration save — mirrors denyTask. EC cancellation
    // stops the generator but can't abort an already-sent PATCH; a late save against the doc we're
    // about to destroy 404s harmlessly, so awaiting it would only add latency. Then await destroy.
    void this.saveExpirationDatesTask.cancelAll();
    this.deleteErrorMessage = null;
    try {
      // DELETE /api/v3/visitor-documents/:id — cascades the doc off both the invite and the
      // visitor profile (UserDocument dependent: :destroy + ensure_document_link_exists).
      await doc.destroyRecord();
      // The destroy round-trip can outlive the card (route transition / parent re-render); don't
      // write tracked state on a torn-down component.
      if (this.isDestroying || this.isDestroyed) return;
      // Reset any stale manage-upload phase: after an upload→delete on the SAME persisted card
      // (the list keys by template id, so the instance survives the reload), uploadPhase would
      // still read 'completed' and keep showManageUpload false — leaving no dropzone to re-upload
      // the now-removed required doc.
      this.uploadPhase = 'idle';
      this.showDeleteConfirm = false;
      this.args.onStatusChange?.();
    } catch (e) {
      if (didCancel(e)) return;
      // Return the just-failed destroy to a clean baseline so a retry re-issues DELETE — a
      // deleted-dirty/errored record would otherwise no-op or throw and wedge the modal.
      doc.rollbackAttributes();
      if (!this.isDestroying && !this.isDestroyed) {
        this.deleteErrorMessage = 'Could not remove the document. Please try again.';
      }
      throw e; // surface the failure to telemetry, matching denyTask/approveTask/resetTask
    }
  });

  <template>
    <UiCard
      @border={{false}}
      class="rounded-lg p-6 mb-4 overflow-visible !bg-carbon-5 !shadow-none border border-carbon-10"
      data-test-document-review-card
      data-test-admin-prereg-document={{this.adminPreregTemplateId}}
      ...attributes
    >
      {{! Header: title (with the status badge stacked beneath it) on the left; the
          manage delete affordance is a trash icon top-right. Create mode renders inside the
          "Required documents" <h3> section, so its title is an <h4> to keep heading levels nested. }}
      <div class="flex items-start justify-between mb-4">
        <div>
          {{#if this.isCreateMode}}
            <h4 class="text-base font-medium m-0" data-test-document-title>{{this.title}}</h4>
          {{else}}
            <h3 class="text-lg font-semibold m-0" data-test-document-title>{{this.title}}</h3>
          {{/if}}
          {{#if this.uploadDate}}
            <p class="text-sm text-carbon-50 mt-1 mb-0" data-test-upload-date>
              Uploaded
              {{this.uploadDate}}
            </p>
          {{/if}}
          {{#if this.showStatusBadge}}
            {{! -ml-1 cancels UiPill's built-in ml-1 so the badge aligns flush-left under the upload time. }}
            <div class="mt-2 -ml-1">
              <DocumentStatusBadge @status={{this.status}} />
            </div>
          {{/if}}
        </div>
        {{#if this.showDeleteAffordance}}
          <button
            type="button"
            class="shrink-0 -mt-1 -mr-1 flex items-center justify-center p-1 rounded text-carbon-70 transition-opacity hover:opacity-75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gem-60 focus-visible:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-test-document-delete
            aria-label="Remove document"
            disabled={{this.deleteDocumentTask.isRunning}}
            {{on "click" this.openDeleteConfirm}}
          >
            {{svgJar "trash-16" width="20" height="20" class="[&_path]:fill-current"}}
          </button>
        {{else if (and this.isCreateMode (or this.createCompleted this.createErrored))}}
          {{! Create-mode removal uses the same trash icon + top-right placement as the manage
              delete affordance (replaces the former "Remove" text link); the action resets the draft. }}
          <button
            type="button"
            class="shrink-0 -mt-1 -mr-1 flex items-center justify-center p-1 rounded text-carbon-70 transition-opacity hover:opacity-75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gem-60 focus-visible:ring-offset-2"
            data-test-remove-draft
            aria-label="Remove uploaded document"
            {{on "click" this.resetFile}}
          >
            {{svgJar "trash-16" width="20" height="20" class="[&_path]:fill-current"}}
          </button>
        {{/if}}
      </div>

      {{#if this.isCreateMode}}
        {{! ===== CREATE MODE: dropzone -> upload -> parse -> rows ===== }}

        {{! Persistent live region: announces both the in-progress phase and completion
            ("editable rows appeared") to assistive tech. The visible parsing block is
            aria-hidden; errors announce via role="alert". Present-then-filled (not
            inserted-with-content) for reliable SR delivery. }}
        <span class="sr-only" role="status" aria-live="polite" data-test-draft-status-announcement>
          {{if this.createInProgress this.parsingLabel}}{{if
            this.createCompleted
            "Document ready. Review the expiration dates below."
          }}
        </span>

        {{! In-memory preview of the selected file (hoisted — one copy shared by every state):
            the rendered content (image, or PDF first page via a blob-URL iframe) is clipped to a
            166px Carbon/10 frame showing the document top. }}
        {{#if this.selectedFile}}
          {{! The in-memory preview floats inside a gray rounded container so it
              reads as a contained document rather than a bare bordered box. }}
          <div class="rounded-lg bg-carbon-10 p-2 mb-4" data-test-draft-preview>
            <div class="relative rounded-md overflow-hidden bg-white">
              {{#if this.hasImagePreview}}
                <img
                  src={{this.previewDataUrl}}
                  alt="Preview of uploaded document"
                  class="w-full h-[166px] object-cover object-top"
                  data-private
                />
              {{else if this.createPreviewPdfUrl}}
                <iframe
                  src={{this.createPreviewPdfUrl}}
                  title="Document preview"
                  class="w-full h-[166px] border-0 pointer-events-none"
                  data-private
                ></iframe>
              {{else}}
                {{! Fallback (URL not yet ready / unexpected type): name the file rather than show nothing. }}
                <div class="flex items-center gap-3 p-4">
                  <span class="flex-none">{{svgJar "document" height="24"}}</span>
                  <span class="flex-1 font-medium text-carbon-70 overflow-hidden" data-test-draft-filename data-private>
                    {{this.selectedFile.name}}
                  </span>
                </div>
              {{/if}}
              {{#if (or this.hasImagePreview this.createPreviewPdfUrl)}}
                {{! Bottom gray lifts up over the document so the preview reads as a peek, not a full page. }}
                <div
                  class="absolute inset-x-0 bottom-0 h-12 bg-carbon-10"
                  aria-hidden="true"
                  data-test-draft-preview-fade
                ></div>
              {{/if}}
            </div>
          </div>
        {{/if}}

        {{#if (or this.createCompleted this.createErrored)}}
          {{! Terminal state (parsed OR failed/timed-out): the preview + editable expiration rows
              show below, and removal is the trash icon in the card header (above). This branch is
              intentionally empty so the dropzone does not re-appear once a draft exists. }}
        {{else if this.createInProgress}}
          <p
            class="flex items-center gap-2 text-sm text-carbon-50 mt-0 mb-4"
            data-test-admin-prereg-document-parsing
            aria-hidden="true"
          >
            <img src="/assets/images/progress-small-gray.svg" alt="" aria-hidden="true" class="w-4 h-4" />
            {{this.parsingLabel}}
          </p>
        {{else}}
          {{! Dropzone — DirectUploader handles drag/drop, type + size validation, and preview }}
          <DirectUploader
            @accept={{this.acceptedFileTypes}}
            @validType={{this.acceptedMimeRegex}}
            @validSize={{this.maxFileSizeBytes}}
            @autoUpload={{false}}
            @enqueue={{false}}
            @showResetButton={{false}}
            @showErrorState={{false}}
            @onValidate={{this.handleValidate}}
            @onFilePreview={{this.handleFilePreview}}
            @onFileSelected={{this.handleFileSelected}}
            @inputId={{this.uploadInputId}}
            @inputAriaLabel={{this.uploadInputAriaLabel}}
            {{! White dropzone card on the gray section (Base/White fill,
                Carbon/10 solid border). Without bg-white the transparent dropzone shows the
                gray section through it. }}
            class="h-40 bg-white !border-solid border-carbon-10"
            data-test-draft-dropzone
          >
            <:label>
              {{! Tied to the input via for/id so the dropzone input has an accessible name
                  and clicking the text opens the file picker. }}
              <label for={{this.uploadInputId}} class="cursor-pointer">
                <span class="btn--link !text-gem-50 text-sm">Upload a document</span>
                <span class="!text-sm">or drag and drop here</span>
              </label>
            </:label>
          </DirectUploader>
          <p class="text-sm text-carbon-40 mt-2 mb-0" data-test-accepted-formats>
            Accepted formats: .pdf, .jpg, .png.
          </p>
        {{/if}}

        {{#if this.uploadErrorMessage}}
          <p class="text-xs text-red-50 mt-1 mb-2" data-test-draft-error role="alert" aria-live="assertive">
            {{this.uploadErrorMessage}}
          </p>
        {{/if}}

      {{else if @isUploaded}}
        {{! ===== REVIEW MODE: uploaded-document preview ===== }}
        {{#if this.isDiscarded}}
          <div class="rounded-md border border-carbon-10 overflow-hidden mb-4 bg-carbon-5" data-test-document-discarded>
            <div class="w-full min-h-80 flex items-center justify-center">
              <span class="text-carbon-70 text-sm font-normal italic">Document discarded</span>
            </div>
          </div>
        {{else if this.externalAttachmentUrl}}
          {{! Floating document in a gray frame — same structure as the create-mode
              preview: outer Carbon/10 p-2 frame (even border on all four sides) wrapping
              the inner white document, clipped to a compact 166px peek. }}
          <div class="rounded-lg bg-carbon-10 p-2 mb-4" data-test-document-preview>
            <div class="group relative rounded-md overflow-hidden bg-white">
              {{#if this.isPdf}}
                <iframe
                  src={{this.pdfViewerUrl}}
                  title={{this.documentFileName}}
                  class="w-full h-[166px] border-0"
                  data-private
                />
                {{! Transparent div captures hover over iframe so group-hover triggers }}
                <div class="absolute inset-0"></div>
              {{else}}
                <img
                  src={{this.externalAttachmentUrl}}
                  alt={{this.title}}
                  class="w-full h-[166px] object-cover object-top"
                  data-private
                  {{on "error" this.handleImageError}}
                />
              {{/if}}
              {{! Bottom gray lifts up over the document so the preview reads as a peek, not a full page. }}
              <div
                class="absolute inset-x-0 bottom-0 h-12 bg-carbon-10 pointer-events-none"
                aria-hidden="true"
                data-test-document-preview-fade
              ></div>
              <div class={{this.mediaOverlayClass}}>
                <a
                  href={{this.externalAttachmentUrl}}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  class="btn btn--small btn--outline flex items-center gap-2 !bg-carbon-5 !rounded-md pointer-events-auto"
                  data-test-download-button
                >
                  {{svgJar "download" width="14" height="14" class="shrink-0 -mt-1"}}
                  <span>Download</span>
                </a>
                <button
                  type="button"
                  class="btn btn--small btn--outline flex items-center gap-2 !bg-carbon-5 !rounded-md pointer-events-auto"
                  {{on "click" this.openDocument}}
                  data-test-open-button
                >
                  {{svgJar "expand-corners" width="12" height="12" class="shrink-0 -mt-1"}}
                  <span>Open</span>
                </button>
              </div>
            </div>
          </div>

          {{#if this.showPreviewModal}}
            <DocumentPreviewModal
              @documents={{this.previewDocuments}}
              @initialIndex={{0}}
              @onClose={{this.closePreviewModal}}
            />
          {{/if}}
        {{/if}}
      {{else if this.showManageUpload}}
        {{! ===== MANAGE MODE: direct upload (no draft/convert; the tab reload shows the doc) ===== }}
        {{! Persistent live region announces the in-progress phase to assistive tech. }}
        <span class="sr-only" role="status" aria-live="polite" data-test-manage-upload-announcement>
          {{if this.manageUploadInProgress this.parsingLabel}}
        </span>

        {{#if this.manageUploadInProgress}}
          <p
            class="flex items-center gap-2 text-sm text-carbon-50 mt-0 mb-4"
            data-test-admin-prereg-document-parsing
            aria-hidden="true"
          >
            <img src="/assets/images/progress-small-gray.svg" alt="" aria-hidden="true" class="w-4 h-4" />
            {{this.parsingLabel}}
          </p>
        {{else}}
          {{! Same DirectUploader dropzone as create — drag/drop + type/size validation }}
          <DirectUploader
            @accept={{this.acceptedFileTypes}}
            @validType={{this.acceptedMimeRegex}}
            @validSize={{this.maxFileSizeBytes}}
            @autoUpload={{false}}
            @enqueue={{false}}
            @showResetButton={{false}}
            @showErrorState={{false}}
            @onValidate={{this.handleValidate}}
            @onFilePreview={{this.handleFilePreview}}
            @onFileSelected={{this.handleFileSelected}}
            @inputId={{this.uploadInputId}}
            @inputAriaLabel={{this.uploadInputAriaLabel}}
            class="h-40 bg-white !border-solid border-carbon-10"
            data-test-manage-dropzone
          >
            <:label>
              <label for={{this.uploadInputId}} class="cursor-pointer">
                <span class="btn--link !text-gem-50 text-sm">Upload a document</span>
                <span class="!text-sm">or drag and drop here</span>
              </label>
            </:label>
          </DirectUploader>
          <p class="text-sm text-carbon-40 mt-2 mb-0" data-test-accepted-formats>
            Accepted formats: .pdf, .jpg, .png.
          </p>
        {{/if}}

        {{#if this.uploadErrorMessage}}
          <p class="text-xs text-red-50 mt-1 mb-2" data-test-manage-upload-error role="alert" aria-live="assertive">
            {{this.uploadErrorMessage}}
          </p>
        {{/if}}
      {{else if this.manageUploadAwaitingReload}}
        {{! Upload succeeded but the tab reload that lands the document hasn't (failed or still
            settling). Offer a manual refresh instead of the misleading "not uploaded" copy — and
            never the dropzone, so a re-drop can't create a duplicate auto-approved doc. }}
        <div class="py-8 text-center text-carbon-50" data-test-manage-upload-awaiting-reload>
          <p class="mb-2">Document uploaded.</p>
          <button
            type="button"
            class="btn--link !text-gem-50 text-sm"
            data-test-manage-upload-refresh
            {{on "click" this.retryManageReload}}
          >
            Refresh to view
          </button>
        </div>
      {{else}}
        {{! Not uploaded placeholder }}
        <div class="py-8 text-center text-carbon-50" data-test-not-uploaded-placeholder>
          Document not yet uploaded
        </div>
      {{/if}}

      {{! ===== Shared editable expiration rows (review-uploaded OR create-completed) ===== }}
      {{#if this.showExpirationRows}}
        {{! template-lint-disable use-ui-card }}
        <div
          class="mt-4 bg-white rounded-lg border border-carbon-10 p-4"
          data-test-expiration-fields
          {{this.captureExpirationFields}}
          {{this.seedFromControlledArgs @status @fieldValues}}
          {{this.seedFromDocumentData @document.inputFieldsData}}
        >
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-carbon-50 leading-none">Expiration date(s)</span>
            <button
              class="text-sm text-gem-50 hover:underline"
              type="button"
              data-test-add-expiration
              {{on "click" this.addExpirationRow}}
            >
              Add
            </button>
          </div>

          {{#if this.parseReturnedNoDates}}
            <p class="text-xs text-carbon-50 mb-2" data-test-no-dates-detected>
              No expiration dates were detected — add them below if this document has any.
            </p>
          {{/if}}

          {{! key by index: edits replace the (immutable) entry objects, and identity keying
              would tear down + recreate every row's DOM on each committed edit — dropping
              keyboard focus mid-entry (name -> date tabbing landed on <body>). }}
          {{#each this.expirationEntries key="@index" as |entry index|}}
            <div class="flex items-center gap-2 mb-3" data-test-expiration-row>
              <input
                type="text"
                value={{entry.name}}
                placeholder="Name"
                aria-label={{this.expirationNameLabel index}}
                class="flex-1 text-sm border rounded px-3 h-10 !mb-0
                  {{if (and (not entry.name) entry.date) 'border-red-50' 'border-carbon-20'}}"
                aria-invalid={{if (and (not entry.name) entry.date) "true" "false"}}
                aria-describedby={{if (and (not entry.name) entry.date) (this.expirationNameErrorId index)}}
                data-test-expiration-name
                data-private
                {{on "change" (fn this.updateExpirationName index)}}
              />
              {{! aria-describedby reason for the immediate aria-invalid; the visible row-level alert
                  below stays deferred (fires only once focus leaves the rows) to avoid interrupting mid-entry. }}
              {{#if (and (not entry.name) entry.date)}}
                <p id={{this.expirationNameErrorId index}} class="sr-only" data-test-expiration-name-error>
                  Name is required for this expiration date.
                </p>
              {{/if}}
              <div class="relative">
                <button
                  type="button"
                  class="flex items-center justify-start gap-1.5 w-[130px] px-3 text-sm border rounded bg-white cursor-pointer transition-colors duration-150 hover:border-carbon-40 h-10
                    {{if (eq this.openCalendar index) 'border-gem-50' 'border-carbon-20'}}"
                  aria-label={{this.expirationDateLabel index}}
                  aria-haspopup="true"
                  aria-expanded={{if (eq this.openCalendar index) "true" "false"}}
                  data-test-expiration-date
                  {{on "click" (fn this.toggleCalendar index)}}
                >
                  <span class="flex-none flex items-center text-carbon-40">{{svgJar
                      "calendar-16"
                      width="16"
                      height="16"
                    }}</span>
                  <span class="{{unless entry.date 'text-carbon-40'}}">{{this.formatDate entry.date}}</span>
                </button>
                {{! template-lint-disable use-ui-card }}
                {{#if (eq this.openCalendar index)}}
                  <div
                    class="absolute right-0 z-50 mt-1 p-3 bg-white border border-carbon-20 rounded-lg shadow-lg"
                    data-test-expiration-calendar
                  >
                    <EnvoyOneUpCalendar
                      @selectedDate={{entry.date}}
                      @onSelectDate={{fn this.selectExpirationDate index}}
                    />
                  </div>
                {{/if}}
              </div>
              {{#if this.canRemoveRow}}
                <button
                  class="flex-none w-8 h-10 flex items-center justify-center text-carbon-70 transition-opacity hover:opacity-75"
                  type="button"
                  data-test-remove-expiration
                  {{on "click" (fn this.removeExpirationRow index)}}
                >
                  {{svgJar "cross-black" width="16" height="16" class="[&_path]:fill-current"}}
                </button>
              {{else}}
                <div class="flex-none w-8 h-10"></div>
              {{/if}}
            </div>
          {{/each}}
          {{#if this.showIncompleteRowError}}
            <p class="text-xs text-red-50 mt-1" data-test-expiration-validation role="alert" aria-live="assertive">
              Both name and date are required for each entry.
            </p>
          {{/if}}
          {{#if this.saveErrorMessage}}
            <p class="text-xs text-red-50 mt-1" data-test-expiration-save-error role="alert" aria-live="assertive">
              {{this.saveErrorMessage}}
            </p>
          {{/if}}
        </div>
      {{/if}}

      {{! ===== Approval actions (approve / deny / undo) — in review AND manage for a still-pending
          or decided doc (an admin-uploaded doc is already approved → none show); never on create. ===== }}
      {{#if (and @isUploaded (not this.isCreateMode))}}
        {{#if this.isPending}}
          {{#if (and this.requiresExpirationDate (not this.hasAnyCompleteDate))}}
            <p class="text-xs text-red-50 mt-2 mb-0" data-test-expiration-required role="alert" aria-live="assertive">
              At least one expiration date is required before approving.
            </p>
          {{/if}}
          <div class="flex gap-4 mt-4" data-test-document-actions>
            <button
              class="flex-1 flex items-center justify-center gap-2 px-4 py-[11px] bg-white border border-carbon-20 rounded-md text-base font-medium text-carbon-70 hover:bg-cilantro-10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-test-approve-button
              disabled={{this.approveDisabled}}
              type="button"
              {{on "click" (perform this.approveTask)}}
            >
              {{svgJar "checkmark-green" width="16" height="16"}}
              Approve
            </button>
            <button
              class="flex-1 flex items-center justify-center gap-2 px-4 py-[11px] bg-white border border-carbon-20 rounded-md text-base font-medium text-carbon-70 hover:bg-red-10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-test-deny-button
              disabled={{this.denyTask.isRunning}}
              type="button"
              {{on "click" (perform this.denyTask)}}
            >
              {{svgJar "cross-small-red" width="16" height="16"}}
              Deny
            </button>
          </div>
        {{else if (and this.isDecided @showUndo)}}
          <div class="flex mt-4" data-test-document-actions>
            <button
              class="px-4 py-[11px] bg-white border border-carbon-20 rounded-md text-sm font-medium text-carbon-70 hover:bg-carbon-5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-test-undo-button
              disabled={{this.resetTask.isRunning}}
              type="button"
              {{on "click" (perform this.resetTask)}}
            >
              Undo
            </button>
          </div>
        {{/if}}
      {{/if}}

      {{! Manage-mode delete is triggered by the trash icon in the header (above); this just
          hosts the confirmation modal, which the icon opens. Admin docs are auto-approved. }}
      {{#if this.showDeleteAffordance}}
        <DocumentDeleteConfirmationModal
          @isOpen={{this.showDeleteConfirm}}
          @onConfirm={{perform this.deleteDocumentTask}}
          @onCancel={{this.closeDeleteConfirm}}
          @isDeleting={{this.deleteDocumentTask.isRunning}}
          @errorMessage={{this.deleteErrorMessage}}
        />
      {{/if}}
    </UiCard>
  </template>
}
